import type { NeighborhoodAnalysis } from '@popety-io/types'
import type { BuildingConformityResult } from '../NeighborhoodContextTab.types'

export const calculateBuildingConformity = (
  neighborhoodAnalysis: NeighborhoodAnalysis | null,
  building: any
): BuildingConformityResult | null => {
  if (!neighborhoodAnalysis || !building) {
    return null
  }

  const quartier_profile =
    neighborhoodAnalysis.quartier_analysis?.quartier_profile
  const statistics = neighborhoodAnalysis.statistics

  // Check if we have minimum required data
  if (!quartier_profile || !statistics) {
    return null
  }

  // Get building data
  const buildingYear = building.building_year || 0
  const buildingHeight = building.building_height || building.height || 0
  const buildingArea = building.area || building.building_area || 0
  const buildingFloors = building.floor_nb || 0
  const buildingCategory = building.category || ''

  // 1. Temporal Conformity (40%)
  let temporalScore = 0
  if (buildingYear > 0 && quartier_profile.typical_building_year > 0) {
    const yearDiff = Math.abs(
      buildingYear - quartier_profile.typical_building_year
    )
    if (yearDiff <= 5) temporalScore = 100
    else if (yearDiff <= 10) temporalScore = 75
    else if (yearDiff <= 20) temporalScore = 50
    else if (yearDiff <= 30) temporalScore = 25
    else temporalScore = 0
  }

  // 2. Physical Conformity (40%) - average of height, area, floors
  const physicalScores: number[] = []

  // Height conformity
  if (
    buildingHeight > 0 &&
    quartier_profile.typical_height &&
    quartier_profile.typical_height > 0
  ) {
    const heightDiff =
      Math.abs(buildingHeight - quartier_profile.typical_height) /
      quartier_profile.typical_height
    if (heightDiff <= 0.2) physicalScores.push(100)
    else if (heightDiff <= 0.4)
      physicalScores.push(50 + (0.4 - heightDiff) * 250)
    else physicalScores.push(Math.max(0, 50 - (heightDiff - 0.4) * 100))
  }

  // Area conformity
  if (
    buildingArea > 0 &&
    quartier_profile.typical_area &&
    quartier_profile.typical_area > 0
  ) {
    const areaDiff =
      Math.abs(buildingArea - quartier_profile.typical_area) /
      quartier_profile.typical_area
    if (areaDiff <= 0.3) physicalScores.push(100)
    else if (areaDiff <= 0.6)
      physicalScores.push(50 + (0.6 - areaDiff) * 166.67)
    else physicalScores.push(Math.max(0, 50 - (areaDiff - 0.6) * 100))
  }

  // Floor conformity
  if (
    buildingFloors > 0 &&
    statistics.floor_count &&
    statistics.floor_count.mean > 0
  ) {
    const floorDiff = Math.abs(buildingFloors - statistics.floor_count.mean)
    if (floorDiff === 0) physicalScores.push(100)
    else if (floorDiff <= 1) physicalScores.push(75)
    else if (floorDiff <= 2) physicalScores.push(50)
    else physicalScores.push(Math.max(0, 50 - (floorDiff - 2) * 10))
  }

  const physicalScore =
    physicalScores.length > 0
      ? physicalScores.reduce((sum, score) => sum + score, 0) /
        physicalScores.length
      : 0

  // 3. Typological Conformity (20%)
  const typologicalScore =
    buildingCategory && quartier_profile.typical_category
      ? buildingCategory === quartier_profile.typical_category
        ? 100
        : 0
      : 0

  // Calculate weighted overall score
  const overallScore =
    temporalScore * 0.4 + physicalScore * 0.4 + typologicalScore * 0.2

  const result = {
    score: Math.round(overallScore * 10) / 10,
    temporal: Math.round(temporalScore * 10) / 10,
    physical: Math.round(physicalScore * 10) / 10,
    typological: Math.round(typologicalScore * 10) / 10,
  }

  return result
}
